// src/components/AutoCycle.tsx
// Unified Auto-Cycle component for SOXL/SOXS with rotation support

import { useState, useEffect, useCallback, useRef } from "react";
import LoadingSpinner from "./LoadingSpinner";
import TickerPrice from "./TickerPrice";
import { buildApiUrl } from "../api/etfApi";
import {
  getAutoCycleStatus,
  updateAutoCycleConfig,
  acknowledgeAutoCycleAlert,
  getAutoCycleLedger,
  recordAutoCycleRealTrade,
  forceCloseAutoCycle,
  closeAutoCycleLeg,
  runAutoCycleBacktest,
  getIntradayStatus,
  updateIntradayConfig,
  reconfirmIntraday,
  type AutoCycleStatusResponse,
  type AutoCycleConfigRequest,
  type AutoCycleTradeLedgerEntry,
  type IntradayStatusResponse,
} from "../api/autoCycleApi";

function AutoCycle() {
  const [status, setStatus] = useState<AutoCycleStatusResponse | null>(null);
  // Start with loading=false - we'll only set it to true on the very first load attempt
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ledger, setLedger] = useState<AutoCycleTradeLedgerEntry[]>([]);
  const [ledgerEngine, setLedgerEngine] = useState<"SOXL" | "SOXS" | undefined>(undefined);
  const [ledgerType, setLedgerType] = useState<"simulation" | "real">("simulation");
  const [ledgerLoading, setLedgerLoading] = useState(false);

  // Configuration state
  const [enabled, setEnabled] = useState(false);
  const [rotationMode, setRotationMode] = useState<"soxl_only" | "soxs_only" | "auto_rotate">("soxl_only");
  const [mode, setMode] = useState<"simulation" | "tracking">("tracking");
  const [accountValue, setAccountValue] = useState(10000);
  const [maxPositionPct, setMaxPositionPct] = useState(50);
  const [riskProfile, setRiskProfile] = useState<"conservative" | "moderate" | "aggressive">("moderate");
  const [crashGuardEnabled, setCrashGuardEnabled] = useState(true);
  
  // Tracking inputs for both engines
  const [soxlEntryPrice, setSoxlEntryPrice] = useState<number | undefined>();
  const [soxlShares, setSoxlShares] = useState<number | undefined>();
  const [soxsEntryPrice, setSoxsEntryPrice] = useState<number | undefined>();
  const [soxsShares, setSoxsShares] = useState<number | undefined>();

  // Backtest state
  const [backtestMode, setBacktestMode] = useState(false);
  const [backtestEngine, setBacktestEngine] = useState<"SOXL" | "SOXS">("SOXL");
  const [backtestStartDate, setBacktestStartDate] = useState("");
  const [backtestResults, setBacktestResults] = useState<any>(null);
  const [backtestLoading, setBacktestLoading] = useState(false);
  const [backtestError, setBacktestError] = useState<string | null>(null);
  const [backtestIntradayOverlay, setBacktestIntradayOverlay] = useState(false);

  // Intraday Execution Layer state
  const [intradayStatus, setIntradayStatus] = useState<IntradayStatusResponse | null>(null);
  const [intradayEnabled, setIntradayEnabled] = useState(false);
  const [intradayProfile, setIntradayProfile] = useState<"conservative" | "moderate" | "aggressive">("moderate");
  const [intradayTimeframe, setIntradayTimeframe] = useState<"5m" | "15m">("5m");
  const [intradayLoading, setIntradayLoading] = useState(false);

  // Manual trade recording
  const [showRecordTrade, setShowRecordTrade] = useState(false);
  const [recordEngine, setRecordEngine] = useState<"SOXL" | "SOXS">("SOXL");
  const [recordAction, setRecordAction] = useState<"BUY" | "SELL">("BUY");
  const [recordShares, setRecordShares] = useState("");
  const [recordPrice, setRecordPrice] = useState("");
  const [recordNotes, setRecordNotes] = useState("");

  // Force close
  const [showForceClose, setShowForceClose] = useState(false);
  const [forceClosePrice, setForceClosePrice] = useState("");
  const [forceCloseNotes, setForceCloseNotes] = useState("");
  const [forceCloseLoading, setForceCloseLoading] = useState(false);

  // Close leg
  const [showCloseLeg, setShowCloseLeg] = useState(false);
  const [closeLegId, setCloseLegId] = useState<string | null>(null);
  const [closeLegShares, setCloseLegShares] = useState("");
  const [closeLegPrice, setCloseLegPrice] = useState("");
  const [closeLegNotes, setCloseLegNotes] = useState("");
  const [closeLegLoading, setCloseLegLoading] = useState(false);
  
  const [lastAcknowledgedBadge, setLastAcknowledgedBadge] = useState(false);
  const isLoadingRef = useRef(false);
  // Track if this is the initial load (to prevent showing loading spinner on polling updates)
  const isInitialLoadRef = useRef(true);
  // Track if we've ever successfully loaded data (persists across remounts via sessionStorage)
  const hasEverLoadedRef = useRef((() => {
    try {
      const stored = sessionStorage.getItem('auto_cycle_has_loaded');
      return stored === 'true';
    } catch {
      return false;
    }
  })());
  
  // Wrapper to prevent setLoading(true) after initial load - MUST be defined after isInitialLoadRef
  const setLoadingSafe = useCallback((value: boolean) => {
    if (value === true && !isInitialLoadRef.current) {
      console.warn(`[AutoCycle] Attempted to set loading=true after initial load - BLOCKED`);
      return;
    }
    setLoading(value);
  }, []);

  // Price data state for both engines
  const [soxlPrice, setSoxlPrice] = useState<number | null>(null);
  const [soxlPreviousPrice, setSoxlPreviousPrice] = useState<number | null>(null);
  const [soxlPreMarketPrice, setSoxlPreMarketPrice] = useState<number | null>(null);
  const [soxlPreMarketChange, setSoxlPreMarketChange] = useState<number | null>(null);
  const [soxlPostMarketPrice, setSoxlPostMarketPrice] = useState<number | null>(null);
  const [soxlPostMarketChange, setSoxlPostMarketChange] = useState<number | null>(null);
  const [soxlIsPrePostMarket, setSoxlIsPrePostMarket] = useState<boolean>(false);
  
  const [soxsPrice, setSoxsPrice] = useState<number | null>(null);
  const [soxsPreviousPrice, setSoxsPreviousPrice] = useState<number | null>(null);
  const [soxsPreMarketPrice, setSoxsPreMarketPrice] = useState<number | null>(null);
  const [soxsPreMarketChange, setSoxsPreMarketChange] = useState<number | null>(null);
  const [soxsPostMarketPrice, setSoxsPostMarketPrice] = useState<number | null>(null);
  const [soxsPostMarketChange, setSoxsPostMarketChange] = useState<number | null>(null);
  const [soxsIsPrePostMarket, setSoxsIsPrePostMarket] = useState<boolean>(false);
  
  const [marketStatus, setMarketStatus] = useState<string>("Market Closed");

  // Load status
  const loadStatus = useCallback(async () => {
    if (isLoadingRef.current) {
      console.log(`[AutoCycle] loadStatus already in progress, skipping...`);
      return;
    }
    
    isLoadingRef.current = true;
    
    // CRITICAL: Never show loading spinner after initial load OR if we've ever loaded before
    const isInitialLoad = isInitialLoadRef.current && !hasEverLoadedRef.current;
    if (isInitialLoad) {
      console.log(`[AutoCycle] Initial load - showing spinner`);
    } else {
      console.log(`[AutoCycle] Polling update (hasEverLoaded=${hasEverLoadedRef.current}) - NOT showing spinner`);
    }
    
    try {
      // Only clear error on initial load, not on polling updates
      if (isInitialLoad) {
        setError(null);
      }
      // ONLY show loading spinner on the very first load, NEVER during polling
      if (isInitialLoad) {
        setLoadingSafe(true);
      }
      
      const data = await getAutoCycleStatus();
      
      // CRITICAL: If this is initial load and we got data, immediately mark as complete
      if (isInitialLoad) {
        isInitialLoadRef.current = false;
        hasEverLoadedRef.current = true;
        try {
          sessionStorage.setItem('auto_cycle_has_loaded', 'true');
        } catch (e) {
          // Ignore sessionStorage errors
        }
        setLoadingSafe(false);
      }
      
      // Only update status if data actually changed (deep comparison of key fields)
      setStatus(prevStatus => {
        if (!prevStatus) return data;
        
        // Compare key fields that affect the display
        const prevRotation = prevStatus.rotation_state;
        const newRotation = data.rotation_state;
        const prevActive = prevStatus.active_engine_status;
        const newActive = data.active_engine_status;
        
        // Check if any critical display fields changed
        const hasChanged = 
          prevStatus.badge_state !== data.badge_state ||
          prevRotation.rotation_mode !== newRotation.rotation_mode ||
          prevRotation.risk_profile !== newRotation.risk_profile ||
          prevActive?.recommended_action_now !== newActive?.recommended_action_now ||
          prevActive?.recommended_action_detail !== newActive?.recommended_action_detail ||
          prevActive?.engine_state?.enabled !== newActive?.engine_state?.enabled ||
          prevActive?.engine_state?.mode !== newActive?.engine_state?.mode ||
          prevActive?.engine_state?.account_value_usd !== newActive?.engine_state?.account_value_usd ||
          prevActive?.engine_state?.max_position_pct !== newActive?.engine_state?.max_position_pct ||
          prevActive?.cycle_progress?.legs_complete !== newActive?.cycle_progress?.legs_complete ||
          prevActive?.profit_pct_vs_entry !== newActive?.profit_pct_vs_entry ||
          JSON.stringify(prevStatus.soxl_engine_status) !== JSON.stringify(data.soxl_engine_status) ||
          JSON.stringify(prevStatus.soxs_engine_status) !== JSON.stringify(data.soxs_engine_status);
        
        // Only return new object if something actually changed
        return hasChanged ? data : prevStatus;
      });
      
      // Update local state from response - only update if values actually changed
      const rotation = data.rotation_state;
      setEnabled(prev => {
        const newValue = rotation.rotation_mode !== "soxl_only" && rotation.rotation_mode !== "soxs_only" ? 
          (data.active_engine_status?.engine_state?.enabled || false) : 
          (rotation.rotation_mode === "soxl_only" ? (data.soxl_engine_status?.enabled || false) : (data.soxs_engine_status?.enabled || false));
        return prev !== newValue ? newValue : prev;
      });
      
      setRotationMode(prev => {
        const newValue = rotation.rotation_mode;
        return prev !== newValue ? newValue : prev;
      });
      setMode(prev => {
        const newValue = data.active_engine_status?.engine_state?.mode || "tracking";
        return prev !== newValue ? newValue : prev;
      });
      setRiskProfile(prev => {
        const newValue = rotation.risk_profile || "moderate";
        return prev !== newValue ? newValue : prev;
      });
      setCrashGuardEnabled(prev => {
        const raw = rotation.crash_guard_enabled;
        const newValue = raw == null ? true : Boolean(raw);
        return prev !== newValue ? newValue : prev;
      });
      
      if (data.active_engine_status?.engine_state?.account_value_usd !== undefined) {
        const newValue = data.active_engine_status.engine_state.account_value_usd;
        setAccountValue(prev => {
          return prev !== newValue ? newValue : prev;
        });
      }
      if (data.active_engine_status?.engine_state?.max_position_pct !== undefined) {
        const newValue = data.active_engine_status.engine_state.max_position_pct;
        setMaxPositionPct(prev => {
          return prev !== newValue ? newValue : prev;
        });
      }
      
      // Update tracking inputs - only if changed
      if (data.soxl_engine_status) {
        setSoxlEntryPrice(prev => {
          const newValue = data.soxl_engine_status?.avg_entry_price;
          return prev !== newValue ? newValue : prev;
        });
        setSoxlShares(prev => {
          const newValue = data.soxl_engine_status?.real_shares;
          return prev !== newValue ? newValue : prev;
        });
      }
      if (data.soxs_engine_status) {
        setSoxsEntryPrice(prev => {
          const newValue = data.soxs_engine_status?.avg_entry_price;
          return prev !== newValue ? newValue : prev;
        });
        setSoxsShares(prev => {
          const newValue = data.soxs_engine_status?.real_shares;
          return prev !== newValue ? newValue : prev;
        });
      }
    } catch (err: any) {
      console.error("Error loading Auto-Cycle status:", err);
      // Only update error state if it actually changed
      setError(prevError => {
        const newError = err.message || "Failed to load status";
        return prevError !== newError ? newError : prevError;
      });
      // Only set loading to false if we were showing it (initial load)
      if (isInitialLoad) {
        setLoadingSafe(false);
      }
    } finally {
      // Always clear the loading flag, but only update loading state if it was set
      if (isInitialLoad) {
        setLoadingSafe(false);
      }
      isLoadingRef.current = false;
    }
  }, [setLoadingSafe]);

  // Load IEL status
  const loadIntradayStatus = useCallback(async () => {
    try {
      const data = await getIntradayStatus();
      setIntradayStatus(data);
      setIntradayEnabled(data.enabled);
      setIntradayProfile(data.profile);
      setIntradayTimeframe(data.timeframe);
    } catch (err: any) {
      // Silently handle errors - IEL might not be configured yet or endpoint might not be available
      // Only log if it's not a 404 (which means the endpoint doesn't exist yet)
      if (err.message && !err.message.includes("404") && !err.message.includes("Not Found")) {
        console.error("Error loading IEL status:", err);
      }
      // Don't set error state - IEL is optional
    }
  }, []);

  // Load ledger
  const loadLedger = useCallback(async () => {
    try {
      setLedgerLoading(true);
      const data = await getAutoCycleLedger(ledgerEngine, ledgerType, 200);
      setLedger(data.ledger);
    } catch (err: any) {
      console.error("Error loading ledger:", err);
    } finally {
      setLedgerLoading(false);
    }
  }, [ledgerEngine, ledgerType]);

  // Initial load - always load data on mount, but only show spinner if we've never loaded before
  useEffect(() => {
    // Only show spinner on actual initial mount AND if we've never loaded before
    if (isInitialLoadRef.current && !hasEverLoadedRef.current) {
      setLoadingSafe(true);
    }
    // Always attempt to load data, regardless of sessionStorage flag
    loadStatus().catch(err => {
      console.error("Initial load error:", err);
      setError(err.message || "Failed to load status");
      setLoadingSafe(false);
      // Even on error, mark that we've attempted to load
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
        hasEverLoadedRef.current = true;
        try {
          sessionStorage.setItem('auto_cycle_has_loaded', 'true');
        } catch (e) {
          // Ignore sessionStorage errors
        }
      }
    });
    // Load IEL status
    loadIntradayStatus().catch(err => console.error("Error loading IEL status:", err));
  }, [loadStatus, setLoadingSafe, loadIntradayStatus]);

  // Load ledger when filters change
  useEffect(() => {
    if (enabled) {
      loadLedger();
    }
  }, [ledgerType, ledgerEngine, enabled, loadLedger]);

  // Market hours check
  const isMarketOpen = useCallback(() => {
    const now = new Date();
    const etTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
    const dayOfWeek = etTime.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) return false;
    const hours = etTime.getHours();
    const minutes = etTime.getMinutes();
    const timeInMinutes = hours * 60 + minutes;
    return timeInMinutes >= 570 && timeInMinutes < 960;
  }, []);

  // Poll status
  useEffect(() => {
    if (!enabled) return;
    if (!isMarketOpen()) {
      const checkInterval = setInterval(() => {
        if (isMarketOpen()) {
          loadStatus();
          clearInterval(checkInterval);
        }
      }, 300000);
      return () => clearInterval(checkInterval);
    }
    const interval = setInterval(() => {
      if (isMarketOpen()) {
        loadStatus();
      }
    }, 20000);
    return () => clearInterval(interval);
  }, [enabled, isMarketOpen, loadStatus]);

  // Track if we've acknowledged on this status load
  const lastAcknowledgedStatusRef = useRef<string | null>(null);

  // Acknowledge alert when badge appears (only once per badge state change)
  useEffect(() => {
    if (status?.badge_state && !lastAcknowledgedBadge) {
      acknowledgeAutoCycleAlert()
        .then(() => setLastAcknowledgedBadge(true))
        .catch(console.error);
    } else if (!status?.badge_state) {
      setLastAcknowledgedBadge(false);
    }
  }, [status?.badge_state, lastAcknowledgedBadge]);

  // NEW: Auto-acknowledge badge when tab is opened (status loads with badge showing)
  useEffect(() => {
    if (!status) return; // Wait for status to load
    
    // Create a unique key for this status load (using timestamp or a combination)
    const statusKey = status.active_engine_status?.data_meta?.timestamp || `${status.active_engine_status?.recommended_action_now || 'unknown'}-${Date.now()}`;
    
    // If badge is showing and we haven't acknowledged this status load yet
    if (status.badge_state && lastAcknowledgedStatusRef.current !== statusKey) {
      lastAcknowledgedStatusRef.current = statusKey;
      acknowledgeAutoCycleAlert()
        .then(() => {
          setLastAcknowledgedBadge(true);
          // Reload status to reflect the acknowledgment
          loadStatus();
        })
        .catch(console.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]); // Run when status object changes (new load)

  // Update market status
  useEffect(() => {
    const getMarketStatus = () => {
      const now = new Date();
      const etTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
      const dayOfWeek = etTime.getDay();
      const hours = etTime.getHours();
      const minutes = etTime.getMinutes();
      const timeInMinutes = hours * 60 + minutes;
      
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        return "Market Closed";
      }
      
      if (timeInMinutes >= 240 && timeInMinutes < 570) {
        return "Pre-Market";
      } else if (timeInMinutes >= 570 && timeInMinutes < 960) {
        return "Market Open";
      } else if (timeInMinutes >= 960 && timeInMinutes < 1200) {
        return "Post-Market";
      } else {
        return "Market Closed";
      }
    };

    const updateMarketStatus = () => {
      const status = getMarketStatus();
      setMarketStatus(status);
    };

    updateMarketStatus();
    const statusInterval = setInterval(updateMarketStatus, 60000);
    return () => clearInterval(statusInterval);
  }, []);

  // Fetch price data for both SOXL and SOXS
  useEffect(() => {
    const fetchPriceForTicker = async (ticker: "SOXL" | "SOXS") => {
      try {
        const url = buildApiUrl(`api/current-price/${ticker}`);
        const response = await fetch(url);
        if (response.ok) {
          const priceData = await response.json();
          
          const now = new Date();
          const etTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
          const dayOfWeek = etTime.getDay();
          const hours = etTime.getHours();
          const minutes = etTime.getMinutes();
          const timeInMinutes = hours * 60 + minutes;
          const isNormalMarketHours = (dayOfWeek !== 0 && dayOfWeek !== 6) && 
                                      (timeInMinutes >= 570 && timeInMinutes < 960);
          const isPreMarketHours = (dayOfWeek !== 0 && dayOfWeek !== 6) && (timeInMinutes >= 240 && timeInMinutes < 570);
          const isPostMarketHours = (dayOfWeek !== 0 && dayOfWeek !== 6) && (timeInMinutes >= 960 && timeInMinutes < 1200);
          
          // Set current price (backend returns appropriate price based on market status)
          if (priceData.current_price) {
            if (ticker === "SOXL") {
              // Always update previousPrice when we get previous_close from API
              // This ensures color indication is always relative to the last market close
              if (priceData.previous_close !== null && priceData.previous_close !== undefined && priceData.previous_close > 0) {
                setSoxlPreviousPrice(prev => {
                  // Update if we don't have one yet, or if it's significantly different (new trading day)
                  if (prev === null || prev === undefined || Math.abs(prev - priceData.previous_close) > 0.01) {
                    return priceData.previous_close;
                  }
                  return prev; // Keep existing if it's the same (within 1 cent tolerance)
                });
              } else if (soxlPreviousPrice === null || soxlPreviousPrice === undefined) {
                // Fallback: only set to current_price if we truly don't have previous_close
                // This should rarely happen if backend is working correctly
                setSoxlPreviousPrice(priceData.current_price);
              }
              setSoxlPrice(priceData.current_price);
            } else {
              // Always update previousPrice when we get previous_close from API
              // This ensures color indication is always relative to the last market close
              if (priceData.previous_close !== null && priceData.previous_close !== undefined && priceData.previous_close > 0) {
                setSoxsPreviousPrice(prev => {
                  // Update if we don't have one yet, or if it's significantly different (new trading day)
                  if (prev === null || prev === undefined || Math.abs(prev - priceData.previous_close) > 0.01) {
                    return priceData.previous_close;
                  }
                  return prev; // Keep existing if it's the same (within 1 cent tolerance)
                });
              } else if (soxsPreviousPrice === null || soxsPreviousPrice === undefined) {
                // Fallback: only set to current_price if we truly don't have previous_close
                // This should rarely happen if backend is working correctly
                setSoxsPreviousPrice(priceData.current_price);
              }
              setSoxsPrice(priceData.current_price);
            }
          }
          
          const hasPreMarketPrice = priceData.pre_market_price !== null && 
                                   priceData.pre_market_price !== undefined && 
                                   !isNaN(priceData.pre_market_price);
          const hasPostMarketPrice = priceData.post_market_price !== null && 
                                     priceData.post_market_price !== undefined && 
                                     !isNaN(priceData.post_market_price);
          const isPrePostMarket = priceData.is_pre_post_market === true || priceData.is_pre_post_market === "true" || priceData.is_pre_post_market === 1;
          
          if (ticker === "SOXL") {
            setSoxlIsPrePostMarket(isPrePostMarket || false);
            if (hasPreMarketPrice && !isNormalMarketHours && isPreMarketHours) {
              setSoxlPreMarketPrice(priceData.pre_market_price);
              setSoxlPreMarketChange(priceData.pre_market_change !== null && priceData.pre_market_change !== undefined ? priceData.pre_market_change : null);
              setSoxlPostMarketPrice(null);
              setSoxlPostMarketChange(null);
            } else if (hasPostMarketPrice && !isNormalMarketHours && (isPostMarketHours || (isPrePostMarket && !isPreMarketHours))) {
              setSoxlPreMarketPrice(null);
              setSoxlPreMarketChange(null);
              setSoxlPostMarketPrice(priceData.post_market_price);
              setSoxlPostMarketChange(priceData.post_market_change !== null && priceData.post_market_change !== undefined ? priceData.post_market_change : null);
            } else {
              setSoxlPreMarketPrice(null);
              setSoxlPreMarketChange(null);
              setSoxlPostMarketPrice(null);
              setSoxlPostMarketChange(null);
            }
          } else {
            setSoxsIsPrePostMarket(isPrePostMarket || false);
            if (hasPreMarketPrice && !isNormalMarketHours && isPreMarketHours) {
              setSoxsPreMarketPrice(priceData.pre_market_price);
              setSoxsPreMarketChange(priceData.pre_market_change !== null && priceData.pre_market_change !== undefined ? priceData.pre_market_change : null);
              setSoxsPostMarketPrice(null);
              setSoxsPostMarketChange(null);
            } else if (hasPostMarketPrice && !isNormalMarketHours && (isPostMarketHours || (isPrePostMarket && !isPreMarketHours))) {
              setSoxsPreMarketPrice(null);
              setSoxsPreMarketChange(null);
              setSoxsPostMarketPrice(priceData.post_market_price);
              setSoxsPostMarketChange(priceData.post_market_change !== null && priceData.post_market_change !== undefined ? priceData.post_market_change : null);
            } else {
              setSoxsPreMarketPrice(null);
              setSoxsPreMarketChange(null);
              setSoxsPostMarketPrice(null);
              setSoxsPostMarketChange(null);
            }
          }
        }
      } catch (err) {
        console.error(`Error fetching price data for ${ticker}:`, err);
      }
    };

    // Fetch prices for both tickers immediately
    fetchPriceForTicker("SOXL");
    fetchPriceForTicker("SOXS");
    
    // Then update every 30 seconds
    const priceInterval = setInterval(() => {
      fetchPriceForTicker("SOXL");
      fetchPriceForTicker("SOXS");
    }, 30000);
    
    return () => clearInterval(priceInterval);
  }, []); // Empty dependency array - only run on mount

  // Update configuration
  const handleUpdateConfig = async () => {
    try {
      setError(null);
      const config: AutoCycleConfigRequest = {
        enabled,
        rotation_mode: rotationMode,
        mode,
        risk_profile: riskProfile,
        crash_guard_enabled: crashGuardEnabled,
      };

      if (mode === "simulation") {
        config.account_value_usd = accountValue;
        config.max_position_pct = maxPositionPct;
      } else {
        if (soxlEntryPrice !== undefined) config.soxl_tracking_entry_price = soxlEntryPrice;
        if (soxlShares !== undefined) config.soxl_tracking_shares = soxlShares;
        if (soxsEntryPrice !== undefined) config.soxs_tracking_entry_price = soxsEntryPrice;
        if (soxsShares !== undefined) config.soxs_tracking_shares = soxsShares;
      }

      const res = await updateAutoCycleConfig(config);
      if (res?.rotation_state?.crash_guard_enabled !== undefined) {
        setCrashGuardEnabled(res.rotation_state.crash_guard_enabled === true);
      }
      await loadStatus();
    } catch (err: any) {
      setError(err.message || "Failed to update configuration");
    }
  };
  
  // Handle risk profile change
  const handleRiskProfileChange = async (newProfile: "conservative" | "moderate" | "aggressive") => {
    setRiskProfile(newProfile);
    try {
      setError(null);
      await updateAutoCycleConfig({
        enabled,
        rotation_mode: rotationMode,
        mode,
        risk_profile: newProfile,
        crash_guard_enabled: crashGuardEnabled,
      });
      await loadStatus();
    } catch (err: any) {
      setError(err.message || "Failed to update risk profile");
    }
  };

  // Record manual trade
  const handleRecordTrade = async () => {
    try {
      setError(null);
      await recordAutoCycleRealTrade(
        recordEngine,
        recordAction,
        parseFloat(recordShares),
        parseFloat(recordPrice),
        undefined,
        recordNotes || undefined
      );
      setShowRecordTrade(false);
      setRecordShares("");
      setRecordPrice("");
      setRecordNotes("");
      await loadStatus();
      await loadLedger();
    } catch (err: any) {
      setError(err.message || "Failed to record trade");
    }
  };

  // Close individual leg
  const handleCloseLeg = async () => {
    if (!closeLegId) return;
    const price = parseFloat(closeLegPrice);
    if (isNaN(price) || price <= 0) {
      setError("Please enter a valid exit price");
      return;
    }
    const sharesVal = closeLegShares ? parseFloat(closeLegShares) : undefined;
    if ((mode === "tracking" || closeLegId === "partial") && (sharesVal == null || sharesVal <= 0)) {
      setError("Please enter shares to close");
      return;
    }
    try {
      setError(null);
      setCloseLegLoading(true);
      await closeAutoCycleLeg(activeEngine, closeLegId, price, closeLegNotes || undefined, sharesVal);
      setShowCloseLeg(false);
      setCloseLegId(null);
      setCloseLegShares("");
      setCloseLegPrice("");
      setCloseLegNotes("");
      await loadStatus();
      await loadLedger();
      window.dispatchEvent(new CustomEvent("refresh-performance"));
    } catch (err: any) {
      setError(err.message || "Failed to close leg");
    } finally {
      setCloseLegLoading(false);
    }
  };

  // Force close cycle
  const handleForceClose = async () => {
    const price = parseFloat(forceClosePrice);
    if (isNaN(price) || price <= 0) {
      setError("Please enter a valid exit price");
      return;
    }
    try {
      setError(null);
      setForceCloseLoading(true);
      await forceCloseAutoCycle(activeEngine, price, forceCloseNotes || undefined);
      setShowForceClose(false);
      setForceClosePrice("");
      setForceCloseNotes("");
      await loadStatus();
      await loadLedger();
      window.dispatchEvent(new CustomEvent("refresh-performance"));
    } catch (err: any) {
      setError(err.message || "Failed to force-close");
    } finally {
      setForceCloseLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem", minHeight: "200px" }}>
        <LoadingSpinner />
        <div style={{ marginTop: "1rem", color: "#9ca3af" }}>Loading Auto-Cycle...</div>
      </div>
    );
  }

  if (error && !status) {
    return (
      <div style={{ padding: "2rem" }}>
        <h2 style={{ color: "#ffffff", marginBottom: "1rem" }}>Auto Cycle (SOXL / SOXS)</h2>
        <div style={{ padding: "1.5rem", backgroundColor: "#7f1d1d", color: "#ffffff", borderRadius: "8px" }}>
          <h3>Error Loading Auto-Cycle</h3>
          <p>{error}</p>
          <button onClick={() => { setError(null); isInitialLoadRef.current = true; hasEverLoadedRef.current = false; setLoadingSafe(true); loadStatus(); }}
            style={{ padding: "0.75rem 1.5rem", backgroundColor: "#6366f1", color: "#ffffff", border: "none", borderRadius: "6px", cursor: "pointer", marginTop: "1rem" }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // If we have no status and no error, show a loading state (but not the spinner)
  if (!status && !error && !loading) {
    return (
      <div style={{ padding: "1.5rem", maxWidth: "1400px", margin: "0 auto", minHeight: "400px", backgroundColor: "#0f1117" }}>
        <h2 style={{ color: "#ffffff", marginBottom: "1rem" }}>Auto Cycle (SOXL / SOXS)</h2>
        <div style={{ padding: "1rem", color: "#9ca3af" }}>Loading data...</div>
      </div>
    );
  }

  const activeEngine = status?.rotation_state?.active_engine_key || "SOXL";
  const activeStatus = status?.active_engine_status;
  const rotationState = status?.rotation_state;
  const cycleProgress = activeStatus?.cycle_progress;
  const keyLevels = activeStatus?.key_levels;

  return (
    <div style={{ padding: "1.5rem", maxWidth: "1400px", margin: "0 auto", minHeight: "400px", backgroundColor: "#0f1117" }}>
      <h2 style={{ color: "#ffffff", marginBottom: "1rem" }}>Auto Cycle (SOXL / SOXS)</h2>

      {/* Description Panel */}
      <div style={{ padding: "1rem 1.25rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142", borderRadius: "8px", marginBottom: "1.5rem", color: "#d1d5db", fontSize: "0.9rem", lineHeight: "1.6" }}>
        <p style={{ margin: "0 0 0.75rem 0" }}>
          <strong style={{ color: "#ffffff" }}>Phase-State Cycle Engine</strong> — A deterministic, phase-aware trading system supporting SOXL (bull) and SOXS (bear) with automatic rotation based on market regime (QQQ + SMH indicators). The engine uses daily indicators to detect 7 distinct structural phases, each with specific allowed actions.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "0.75rem", marginTop: "0.75rem" }}>
          <div>
            <strong style={{ color: "#9ca3af", fontSize: "0.85rem" }}>Rotation Modes:</strong>
            <ul style={{ margin: "0.5rem 0 0 0", paddingLeft: "1.25rem", color: "#9ca3af" }}>
              <li><strong>SOXL Only:</strong> Run cycle on SOXL only</li>
              <li><strong>SOXS Only:</strong> Run cycle on SOXS only</li>
              <li><strong>Auto-Rotate:</strong> Automatically switch between SOXL/SOXS based on regime and phase</li>
            </ul>
          </div>
          <div>
            <strong style={{ color: "#9ca3af", fontSize: "0.85rem" }}>Core Features:</strong>
            <ul style={{ margin: "0.5rem 0 0 0", paddingLeft: "1.25rem", color: "#9ca3af" }}>
              <li>7-phase structural detection (daily indicators)</li>
              <li>5-leg accumulation schedule</li>
              <li>Phase-aware action gating</li>
              <li>Crash guard protection</li>
              <li>Regime-based rotation (BULL→SOXL, BEAR→SOXS)</li>
              <li>Simulation & Tracking modes</li>
            </ul>
          </div>
          <div>
            <strong style={{ color: "#9ca3af", fontSize: "0.85rem" }}>Intraday Execution Layer (IEL):</strong>
            <ul style={{ margin: "0.5rem 0 0 0", paddingLeft: "1.25rem", color: "#9ca3af" }}>
              <li>Phase-aware timing overlay</li>
              <li>BUY_ADD in Phase 1-2 only</li>
              <li>TRIM in Phase 3-4 only</li>
              <li>EXIT_CONFIRMED in Phase 5 only</li>
              <li>Uses intraday indicators (RSI2, ORB, VWAP)</li>
              <li>Optional overlay (does not replace Auto Cycle)</li>
            </ul>
          </div>
        </div>
        
        <div style={{ marginTop: "1.25rem", paddingTop: "1rem", borderTop: "1px solid #2c3142" }}>
          <strong style={{ color: "#ffffff", fontSize: "0.95rem" }}>Phase Descriptions &amp; Trigger Conditions:</strong>
          <div style={{ color: "#9ca3af", fontSize: "0.8rem", marginTop: "0.5rem", marginBottom: "0.75rem" }}>
            Phase is determined by daily indicators (EMA20/50, RSI14, BB, ATR). The Cycle Phase box shows the current phase plus &quot;Conditions that put you in this phase&quot; and &quot;What can revert to another phase&quot; so you know exactly what drives transitions.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "0.75rem", marginTop: "0.75rem" }}>
            <div style={{ padding: "0.75rem", backgroundColor: "#2c3142", borderRadius: "6px" }}>
              <div style={{ color: "#9ca3af", fontSize: "0.8rem", marginBottom: "0.25rem" }}>Phase 0: BASE BUILDING</div>
              <div style={{ color: "#d1d5db", fontSize: "0.85rem" }}>Watchlist only. Trigger: low BB width, small EMA20 slope, price within 5% of EMA50. Revert: breakout (close above upper BB or range &gt; 1.5× ATR).</div>
            </div>
            <div style={{ padding: "0.75rem", backgroundColor: "#2c3142", borderRadius: "6px" }}>
              <div style={{ color: "#9ca3af", fontSize: "0.8rem", marginBottom: "0.25rem" }}>Phase 1: IMPULSE START</div>
              <div style={{ color: "#d1d5db", fontSize: "0.85rem" }}>Open initial tranche. Trigger: uptrend, close above EMA20, positive slope, breakout (upper BB or range &gt; 1.5× ATR). Revert: close below EMA20 or failure (2+ closes below EMA20).</div>
            </div>
            <div style={{ padding: "0.75rem", backgroundColor: "#2c3142", borderRadius: "6px" }}>
              <div style={{ color: "#9ca3af", fontSize: "0.8rem", marginBottom: "0.25rem" }}>Phase 2: CONTINUATION</div>
              <div style={{ color: "#d1d5db", fontSize: "0.85rem" }}>Add tranches. Trigger: trend intact, RSI14 50–70, ≥3/4 closes above EMA20. Revert: RSI ≥70 → Phase 3; close below EMA20 → Phase 5/6.</div>
            </div>
            <div style={{ padding: "0.75rem", backgroundColor: "#2c3142", borderRadius: "6px" }}>
              <div style={{ color: "#9ca3af", fontSize: "0.8rem", marginBottom: "0.25rem" }}>Phase 3: LATE TREND</div>
              <div style={{ color: "#d1d5db", fontSize: "0.85rem" }}>TRIM only. Trigger: RSI14 ≥70 or price ≥8% above EMA20 or high BB width + accelerating slope. Revert: RSI/price pullback → Phase 2; chop → Phase 4.</div>
            </div>
            <div style={{ padding: "0.75rem", backgroundColor: "#2c3142", borderRadius: "6px" }}>
              <div style={{ color: "#9ca3af", fontSize: "0.8rem", marginBottom: "0.25rem" }}>Phase 4: DISTRIBUTION</div>
              <div style={{ color: "#d1d5db", fontSize: "0.85rem" }}>Trims + exits. Trigger: BB contracting, chop band, or failed breakout. Revert: new breakout → Phase 3; close below EMA20 → Phase 5/6.</div>
            </div>
            <div style={{ padding: "0.75rem", backgroundColor: "#2c3142", borderRadius: "6px" }}>
              <div style={{ color: "#9ca3af", fontSize: "0.8rem", marginBottom: "0.25rem" }}>Phase 5: FAILURE EXIT</div>
              <div style={{ color: "#d1d5db", fontSize: "0.85rem" }}>Exit ASAP. Trigger: close below EMA20 + (slope ≤0 or EMA20 &lt; EMA50) + (2+ closes below EMA20 or lower high). Revert: close reclaims EMA20.</div>
            </div>
            <div style={{ padding: "0.75rem", backgroundColor: "#2c3142", borderRadius: "6px" }}>
              <div style={{ color: "#9ca3af", fontSize: "0.8rem", marginBottom: "0.25rem" }}>Phase 6: FLUSH NO TRADE</div>
              <div style={{ color: "#d1d5db", fontSize: "0.85rem" }}>Exit if holding, then wait. Trigger: RSI14 &lt;30 or close below lower BB, range &gt;1.5× ATR, close below EMA20. Revert: RSI/price recovery.</div>
            </div>
          </div>
          <div style={{ marginTop: "1rem", padding: "0.75rem", backgroundColor: "#1e3a5f", borderRadius: "6px", border: "1px solid #2c5282" }}>
            <strong style={{ color: "#93c5fd", fontSize: "0.9rem" }}>When does rotation (SOXL ↔ SOXS) happen?</strong>
            <div style={{ color: "#d1d5db", fontSize: "0.85rem", marginTop: "0.5rem" }}>
              Only in <strong>Auto-Rotate</strong> mode. Regime is from QQQ + SMH (BULL → SOXL, BEAR → SOXS). Rotation occurs when: (1) regime confirms BULL or BEAR for the required consecutive days (profile-dependent), (2) active engine is not mid-cycle (IDLE or after full exit), and (3) lockout period after last switch has passed. If you rarely see rotations, check rotation mode is Auto-Rotate and that the active engine is often in IDLE so a switch is allowed.
            </div>
          </div>
        </div>
      </div>

      {/* Market status */}
      {enabled && !isMarketOpen() && (
        <div style={{ padding: "0.75rem", backgroundColor: "#1e3a5f", color: "#9ca3af", borderRadius: "8px", marginBottom: "1rem", fontSize: "0.9rem", border: "1px solid #2c5282" }}>
          ⏸️ Market Closed - Updates paused until market opens (9:30 AM - 4:00 PM ET, Mon-Fri)
        </div>
      )}

      {error && (
        <div style={{ padding: "1rem", backgroundColor: "#7f1d1d", color: "#ffffff", borderRadius: "8px", marginBottom: "1rem" }}>
          Error: {error}
        </div>
      )}

      {/* Regime Panel */}
      {rotationState && (
        <div style={{ backgroundColor: "#1a1d29", border: "2px solid #2c3142", borderRadius: "8px", padding: "1.5rem", marginBottom: "1.5rem" }}>
          <h3 style={{ color: "#ffffff", marginBottom: "1rem" }}>Market Regime & Rotation</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" }}>
            <div>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Regime State</div>
              <div style={{ 
                color: rotationState.regime_state === "BULL" ? "#22c55e" : rotationState.regime_state === "BEAR" ? "#ef4444" : "#9ca3af", 
                fontSize: "1.25rem", 
                fontWeight: "600" 
              }}>
                {rotationState.regime_state}
              </div>
            </div>
            <div>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Active Engine</div>
              <div style={{ color: "#ffffff", fontSize: "1.25rem", fontWeight: "600" }}>
                {activeEngine}
              </div>
            </div>
            <div>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem", marginBottom: "0.5rem" }}>Prices</div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <div>
                  <span style={{ color: "#9ca3af", fontSize: "0.875rem", marginRight: "0.5rem" }}>SOXL:</span>
                  {soxlPrice !== null ? (
                    <div style={{ display: "inline-block", fontSize: "1.25rem", fontWeight: 600, letterSpacing: "0.08em" }}>
                      <TickerPrice 
                        currentPrice={soxlPrice} 
                        previousPrice={soxlPreviousPrice}
                        preMarketPrice={soxlPreMarketPrice}
                        preMarketChange={soxlPreMarketChange}
                        postMarketPrice={soxlPostMarketPrice}
                        postMarketChange={soxlPostMarketChange}
                        isPrePostMarket={soxlIsPrePostMarket}
                      />
                    </div>
                  ) : (
                    <span style={{ color: "#6b7280", fontSize: "0.875rem" }}>Loading...</span>
                  )}
                </div>
                <div>
                  <span style={{ color: "#9ca3af", fontSize: "0.875rem", marginRight: "0.5rem" }}>SOXS:</span>
                  {soxsPrice !== null ? (
                    <div style={{ display: "inline-block", fontSize: "1.25rem", fontWeight: 600, letterSpacing: "0.08em" }}>
                      <TickerPrice 
                        currentPrice={soxsPrice} 
                        previousPrice={soxsPreviousPrice}
                        preMarketPrice={soxsPreMarketPrice}
                        preMarketChange={soxsPreMarketChange}
                        postMarketPrice={soxsPostMarketPrice}
                        postMarketChange={soxsPostMarketChange}
                        isPrePostMarket={soxsIsPrePostMarket}
                      />
                    </div>
                  ) : (
                    <span style={{ color: "#6b7280", fontSize: "0.875rem" }}>Loading...</span>
                  )}
                </div>
              </div>
            </div>
            <div>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Market Status</div>
              <div style={{ 
                color: marketStatus === "Market Open" ? "#22c55e" : 
                       marketStatus === "Pre-Market" ? "#f59e0b" :
                       marketStatus === "Post-Market" ? "#3b82f6" : "#6b7280",
                fontSize: "0.875rem",
                fontWeight: "500",
                textTransform: "uppercase",
                letterSpacing: "0.05em"
              }}>
                {marketStatus || "Market Closed"}
              </div>
            </div>
            <div>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Rotation Mode</div>
              <div style={{ color: "#ffffff", fontSize: "1.25rem", fontWeight: "600" }}>
                {rotationMode === "soxl_only" ? "SOXL Only" : rotationMode === "soxs_only" ? "SOXS Only" : "Auto-Rotate"}
              </div>
            </div>
            <div>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Risk Profile</div>
              <div style={{ color: "#ffffff", fontSize: "1.25rem", fontWeight: "600", textTransform: "capitalize" }}>
                {rotationState.risk_profile || riskProfile || "moderate"}
              </div>
            </div>
            {rotationState.switch_lockout_end_date && (
              <div>
                <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Switch Lockout</div>
                <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>
                  Until {new Date(rotationState.switch_lockout_end_date).toLocaleDateString()}
                </div>
              </div>
            )}
            {rotationState.switch_blocked && rotationState.block_reason && rotationMode === "auto_rotate" && (
              <div style={{ gridColumn: "1 / -1", padding: "0.75rem", backgroundColor: "#7f1d1d", borderRadius: "6px", color: "#ffffff", fontSize: "0.875rem" }}>
                ⚠️ Switch Blocked: {rotationState.block_reason}
              </div>
            )}
          </div>
          {rotationState.regime_data && rotationState.regime_data.qqq_close && (
            <div style={{ marginTop: "1rem", padding: "0.75rem", backgroundColor: "#2c3142", borderRadius: "6px", fontSize: "0.875rem", color: "#9ca3af" }}>
              QQQ: ${rotationState.regime_data.qqq_close.toFixed(2)} | 
              SMH: ${rotationState.regime_data.smh_close?.toFixed(2) || "N/A"} | 
              Provider: {rotationState.regime_data.provider || "unknown"}
            </div>
          )}
        </div>
      )}

      {/* Key Levels */}
      {keyLevels && !backtestMode && (
        <div style={{ backgroundColor: "#1a1d29", border: "2px solid #2c3142", borderRadius: "8px", padding: "1.5rem", marginBottom: "1.5rem" }}>
          <h3 style={{ color: "#ffffff", marginBottom: "1rem" }}>Key Levels ({activeEngine})</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "1rem" }}>
            {keyLevels.sma200 !== undefined && keyLevels.sma200 !== null && (
              <div><div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>SMA200</div><div style={{ color: "#ffffff", fontSize: "1.25rem", fontWeight: "600" }}>${keyLevels.sma200.toFixed(2)}</div></div>
            )}
            {keyLevels.ema50 !== undefined && keyLevels.ema50 !== null && (
              <div><div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>EMA50</div><div style={{ color: "#ffffff", fontSize: "1.25rem", fontWeight: "600" }}>${keyLevels.ema50.toFixed(2)}</div></div>
            )}
            {keyLevels.ema20 !== undefined && keyLevels.ema20 !== null && (
              <div><div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>EMA20</div><div style={{ color: "#ffffff", fontSize: "1.25rem", fontWeight: "600" }}>${keyLevels.ema20.toFixed(2)}</div></div>
            )}
            {keyLevels.lower_bb !== undefined && keyLevels.lower_bb !== null && (
              <div><div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Lower BB</div><div style={{ color: "#ffffff", fontSize: "1.25rem", fontWeight: "600" }}>${keyLevels.lower_bb.toFixed(2)}</div></div>
            )}
            {keyLevels.vwap !== undefined && keyLevels.vwap !== null && (
              <div><div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>VWAP</div><div style={{ color: "#ffffff", fontSize: "1.25rem", fontWeight: "600" }}>${keyLevels.vwap.toFixed(2)}</div></div>
            )}
            {keyLevels.rsi2 !== undefined && keyLevels.rsi2 !== null && (
              <div><div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>RSI2</div><div style={{ color: "#ffffff", fontSize: "1.25rem", fontWeight: "600" }}>{keyLevels.rsi2.toFixed(1)}</div></div>
            )}
            {keyLevels.rsi14 !== undefined && keyLevels.rsi14 !== null && (
              <div><div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>RSI14</div><div style={{ color: "#ffffff", fontSize: "1.25rem", fontWeight: "600" }}>{keyLevels.rsi14.toFixed(1)}</div></div>
            )}
          </div>
          {activeStatus?.data_meta && (
            <div style={{ marginTop: "1rem", color: "#9ca3af", fontSize: "0.875rem" }}>
              Data Provider: {activeStatus.data_meta.provider} {activeStatus.data_meta.is_stale && " (Stale)"} {activeStatus.data_meta.is_delayed && " (Delayed)"}
            </div>
          )}
        </div>
      )}

      {/* Risk Status */}
      {activeStatus?.risk_flags?.buys_frozen && (
        <div style={{ padding: "1rem", backgroundColor: "#7f1d1d", color: "#ffffff", borderRadius: "8px", marginBottom: "1rem", border: "2px solid #ef4444" }}>
          <div style={{ fontWeight: "600", marginBottom: "0.5rem", fontSize: "1.1rem" }}>⚠️ BUYS FROZEN (Crash Guard)</div>
          <div style={{ fontSize: "0.9rem", opacity: 0.9 }}>
            {activeStatus.risk_flags.crash_guard_reason || "Volatility/regime risk detected"}
          </div>
        </div>
      )}

      {/* Configuration Panel */}
      <div style={{ backgroundColor: "#1a1d29", border: "2px solid #2c3142", borderRadius: "8px", padding: "1.5rem", marginBottom: "1.5rem" }}>
        <h3 style={{ color: "#ffffff", marginBottom: "1rem" }}>Configuration</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <label style={{ color: "#9ca3af", minWidth: "150px" }}>Enabled:</label>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} style={{ width: "20px", height: "20px" }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <label style={{ color: "#9ca3af", minWidth: "150px" }}>Rotation Mode:</label>
            <select value={rotationMode} onChange={(e) => setRotationMode(e.target.value as any)}
              style={{ padding: "0.5rem", backgroundColor: "#2c3142", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px", minWidth: "200px" }}>
              <option value="soxl_only">SOXL Only</option>
              <option value="soxs_only">SOXS Only</option>
              <option value="auto_rotate">Auto-Rotate (SOXL/SOXS)</option>
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <label style={{ color: "#9ca3af", minWidth: "150px" }}>Risk Profile:</label>
            <select
              value={riskProfile}
              onChange={(e) => handleRiskProfileChange(e.target.value as "conservative" | "moderate" | "aggressive")}
              style={{
                padding: "0.5rem",
                backgroundColor: "#2c3142",
                color: "#ffffff",
                border: "1px solid #3f4758",
                borderRadius: "4px",
                minWidth: "200px",
                cursor: "pointer",
              }}
            >
              <option value="conservative">Conservative</option>
              <option value="moderate">Moderate</option>
              <option value="aggressive">Aggressive</option>
            </select>
            {rotationState?.profile_summary && (
              <div style={{ marginLeft: "0.5rem", padding: "0.25rem 0.5rem", backgroundColor: "#2c3142", borderRadius: "4px", fontSize: "0.8rem", color: "#9ca3af" }}>
                <span style={{ color: "#6366f1" }}>Exits:</span> {rotationState.profile_summary.exit_targets} | 
                <span style={{ color: "#6366f1", marginLeft: "0.25rem" }}>Speed:</span> {rotationState.profile_summary.accumulation_speed}
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <label style={{ color: "#9ca3af", minWidth: "150px" }}>Mode:</label>
            <select value={mode} onChange={(e) => setMode(e.target.value as any)}
              style={{ padding: "0.5rem", backgroundColor: "#2c3142", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px", minWidth: "150px" }}>
              <option value="simulation">Simulation (Auto Trader)</option>
              <option value="tracking">Tracking (Manual Trades)</option>
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <label style={{ color: "#9ca3af", minWidth: "150px" }}>Crash Guard:</label>
            <input type="checkbox" checked={crashGuardEnabled} onChange={(e) => setCrashGuardEnabled(e.target.checked)} style={{ width: "20px", height: "20px" }} />
            <span style={{ color: "#6b7280", fontSize: "0.875rem" }}>Freeze buys when ATR%/drawdown/SMA200 triggers (disable to allow buys in volatile regimes)</span>
          </div>

          {mode === "simulation" && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                <label style={{ color: "#9ca3af", minWidth: "150px" }}>Account Value ($):</label>
                <input type="number" value={accountValue} onChange={(e) => setAccountValue(parseFloat(e.target.value) || 0)}
                  style={{ padding: "0.5rem", backgroundColor: "#2c3142", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px", width: "200px" }} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                <label style={{ color: "#9ca3af", minWidth: "150px" }}>Max Position %:</label>
                <input type="number" value={maxPositionPct} onChange={(e) => setMaxPositionPct(parseFloat(e.target.value) || 0)} min={1} max={100}
                  style={{ padding: "0.5rem", backgroundColor: "#2c3142", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px", width: "200px" }} />
              </div>
              <div style={{ marginTop: "1rem", padding: "1rem", backgroundColor: "#1e3a5f", borderRadius: "8px", border: "1px solid #2c5282" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
                  <label style={{ color: "#9ca3af", minWidth: "150px" }}>Backtest Mode:</label>
                  <input type="checkbox" checked={backtestMode} onChange={(e) => { setBacktestMode(e.target.checked); if (!e.target.checked) { setBacktestResults(null); setBacktestError(null); } }} style={{ width: "20px", height: "20px" }} />
                </div>
                {backtestMode && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                      <label style={{ color: "#9ca3af", minWidth: "150px" }}>Engine:</label>
                      <select value={backtestEngine} onChange={(e) => setBacktestEngine(e.target.value as "SOXL" | "SOXS")}
                        style={{ padding: "0.5rem", backgroundColor: "#2c3142", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px", width: "200px" }}>
                        <option value="SOXL">SOXL</option>
                        <option value="SOXS">SOXS</option>
                      </select>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                      <label style={{ color: "#9ca3af", minWidth: "150px" }}>Start Date:</label>
                      <input type="date" value={backtestStartDate} onChange={(e) => setBacktestStartDate(e.target.value)} max={new Date().toISOString().split('T')[0]}
                        style={{ padding: "0.5rem", backgroundColor: "#2c3142", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px", width: "200px" }} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                      <label style={{ color: "#9ca3af", minWidth: "150px" }}>IEL Overlay:</label>
                      <input type="checkbox" checked={backtestIntradayOverlay} onChange={(e) => setBacktestIntradayOverlay(e.target.checked)} style={{ width: "20px", height: "20px" }} />
                      <span style={{ color: "#6b7280", fontSize: "0.875rem" }}>Use IEL for intraday timing</span>
                    </div>
                    <button onClick={async () => {
                      if (!backtestStartDate) { setBacktestError("Please select a start date"); return; }
                      try {
                        setBacktestLoading(true);
                        setBacktestError(null);
                        const results = await runAutoCycleBacktest(backtestEngine, backtestStartDate, accountValue, maxPositionPct, undefined, riskProfile, backtestIntradayOverlay);
                        setBacktestResults(results);
                      } catch (err: any) {
                        setBacktestError(err.message || "Backtest failed");
                      } finally {
                        setBacktestLoading(false);
                      }
                    }} disabled={backtestLoading || !backtestStartDate}
                      style={{ padding: "0.75rem 1.5rem", backgroundColor: backtestLoading || !backtestStartDate ? "#3f4758" : "#6366f1", color: "#ffffff", border: "none", borderRadius: "6px", cursor: backtestLoading || !backtestStartDate ? "not-allowed" : "pointer", alignSelf: "flex-start" }}>
                      {backtestLoading ? "Running Backtest..." : "Run Backtest"}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {mode === "tracking" && (
            <>
              <div style={{ padding: "1rem", backgroundColor: "#2c3142", borderRadius: "6px", marginTop: "0.5rem" }}>
                <div style={{ color: "#ffffff", fontWeight: "600", marginBottom: "0.75rem" }}>SOXL Tracking</div>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                    <label style={{ color: "#9ca3af", minWidth: "120px" }}>Entry Price ($):</label>
                    <input type="number" value={soxlEntryPrice || ""} onChange={(e) => setSoxlEntryPrice(e.target.value ? parseFloat(e.target.value) : undefined)}
                      style={{ padding: "0.5rem", backgroundColor: "#1a1d29", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px", width: "200px" }} />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                    <label style={{ color: "#9ca3af", minWidth: "120px" }}>Shares:</label>
                    <input type="number" value={soxlShares || ""} onChange={(e) => setSoxlShares(e.target.value ? parseFloat(e.target.value) : undefined)}
                      style={{ padding: "0.5rem", backgroundColor: "#1a1d29", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px", width: "200px" }} />
                  </div>
                </div>
              </div>
              <div style={{ padding: "1rem", backgroundColor: "#2c3142", borderRadius: "6px", marginTop: "0.5rem" }}>
                <div style={{ color: "#ffffff", fontWeight: "600", marginBottom: "0.75rem" }}>SOXS Tracking</div>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                    <label style={{ color: "#9ca3af", minWidth: "120px" }}>Entry Price ($):</label>
                    <input type="number" value={soxsEntryPrice || ""} onChange={(e) => setSoxsEntryPrice(e.target.value ? parseFloat(e.target.value) : undefined)}
                      style={{ padding: "0.5rem", backgroundColor: "#1a1d29", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px", width: "200px" }} />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                    <label style={{ color: "#9ca3af", minWidth: "120px" }}>Shares:</label>
                    <input type="number" value={soxsShares || ""} onChange={(e) => setSoxsShares(e.target.value ? parseFloat(e.target.value) : undefined)}
                      style={{ padding: "0.5rem", backgroundColor: "#1a1d29", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px", width: "200px" }} />
                  </div>
                </div>
              </div>
            </>
          )}

          <button onClick={handleUpdateConfig}
            style={{ padding: "0.75rem 1.5rem", backgroundColor: "#6366f1", color: "#ffffff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600", alignSelf: "flex-start" }}>
            Update Configuration
          </button>
        </div>
      </div>

      {/* Intraday Execution Layer Configuration */}
      <div style={{ backgroundColor: "#1a1d29", border: "2px solid #2c3142", borderRadius: "8px", padding: "1.5rem", marginBottom: "1.5rem" }}>
        <h3 style={{ color: "#ffffff", marginBottom: "1rem" }}>Intraday Execution Layer</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <label style={{ color: "#9ca3af", minWidth: "150px" }}>Enabled:</label>
            <input type="checkbox" checked={intradayEnabled} onChange={(e) => setIntradayEnabled(e.target.checked)} style={{ width: "20px", height: "20px" }} />
            <span style={{ color: "#6b7280", fontSize: "0.875rem" }}>Improves entry/trim timing for Auto Cycle</span>
          </div>
          {intradayEnabled && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                <label style={{ color: "#9ca3af", minWidth: "150px" }}>IEL Profile:</label>
                <select value={intradayProfile} onChange={(e) => setIntradayProfile(e.target.value as any)}
                  style={{ padding: "0.5rem", backgroundColor: "#2c3142", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px", minWidth: "200px" }}>
                  <option value="conservative">Conservative</option>
                  <option value="moderate">Moderate</option>
                  <option value="aggressive">Aggressive</option>
                </select>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                <label style={{ color: "#9ca3af", minWidth: "150px" }}>Timeframe:</label>
                <select value={intradayTimeframe} onChange={(e) => setIntradayTimeframe(e.target.value as any)}
                  style={{ padding: "0.5rem", backgroundColor: "#2c3142", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px", minWidth: "200px" }}>
                  <option value="5m">5-minute</option>
                  <option value="15m">15-minute</option>
                </select>
              </div>
              <button onClick={async () => {
                try {
                  setIntradayLoading(true);
                  await updateIntradayConfig({
                    enabled: intradayEnabled,
                    profile: intradayProfile,
                    timeframe: intradayTimeframe
                  });
                  await loadIntradayStatus();
                } catch (err: any) {
                  console.error("Error updating IEL config:", err);
                  setError(err.message || "Failed to update IEL configuration");
                } finally {
                  setIntradayLoading(false);
                }
              }} disabled={intradayLoading}
                style={{ padding: "0.75rem 1.5rem", backgroundColor: intradayLoading ? "#3f4758" : "#6366f1", color: "#ffffff", border: "none", borderRadius: "6px", cursor: intradayLoading ? "not-allowed" : "pointer", fontWeight: "600", alignSelf: "flex-start" }}>
                {intradayLoading ? "Updating..." : "Update IEL Config"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Intraday Recommendation Card */}
      {intradayStatus && intradayStatus.enabled && !backtestMode && activeStatus?.phase && (
        <div style={{ backgroundColor: "#1a1d29", border: "2px solid #4c1d95", borderRadius: "8px", padding: "1.5rem", marginBottom: "1.5rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <h3 style={{ color: "#ffffff" }}>Intraday Recommendation Now ({intradayStatus.engine_key})</h3>
            <div style={{ padding: "0.5rem 1rem", backgroundColor: 
              activeStatus.phase.allowed_actions.adds ? "#10b981" :
              activeStatus.phase.allowed_actions.trims ? "#7c3aed" :
              activeStatus.phase.allowed_actions.exits ? "#dc2626" : "#6b7280",
              color: "#ffffff", borderRadius: "6px", fontSize: "0.875rem", fontWeight: "600" }}>
              {activeStatus.phase.allowed_actions.adds ? "IEL: BUY_ADD allowed" :
               activeStatus.phase.allowed_actions.trims ? "IEL: TRIM only" :
               activeStatus.phase.allowed_actions.exits ? "IEL: EXIT_CONFIRMED only" : "IEL: HOLD"}
            </div>
            <span style={{ padding: "0.5rem 1rem", backgroundColor: 
              intradayStatus.current_recommendation.action === "BUY_ADD" ? "#10b981" :
              intradayStatus.current_recommendation.action === "TRIM" ? "#f59e0b" :
              intradayStatus.current_recommendation.action === "PAUSE" ? "#ef4444" :
              intradayStatus.current_recommendation.action === "EXIT_CONFIRMED" ? "#dc2626" :
              "#6b7280", 
              color: "#ffffff", borderRadius: "6px", fontWeight: "600" }}>
              {intradayStatus.current_recommendation.action}
            </span>
          </div>
          <div style={{ color: "#d1d5db", lineHeight: "1.6", marginBottom: "1rem" }}>
            {intradayStatus.current_recommendation.reason}
          </div>
          {intradayStatus.current_recommendation.trigger_indicators && intradayStatus.current_recommendation.trigger_indicators.length > 0 && (
            <div style={{ marginBottom: "1rem" }}>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem", marginBottom: "0.5rem" }}>Trigger Indicators:</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                {intradayStatus.current_recommendation.trigger_indicators.map((indicator, idx) => (
                  <span key={idx} style={{ padding: "0.25rem 0.5rem", backgroundColor: "#2c3142", color: "#ffffff", borderRadius: "4px", fontSize: "0.875rem" }}>
                    {indicator}
                  </span>
                ))}
              </div>
            </div>
          )}
          {intradayStatus.current_recommendation.levels && (() => {
            const levels = intradayStatus.current_recommendation.levels;
            const hasAnyLevel = levels.vwap !== null && levels.vwap !== undefined ||
                                levels.ema20 !== null && levels.ema20 !== undefined ||
                                levels.ema50 !== null && levels.ema50 !== undefined ||
                                levels.rsi5 !== null && levels.rsi5 !== undefined ||
                                levels.current_price !== null && levels.current_price !== undefined ||
                                levels.orb_high !== null && levels.orb_high !== undefined ||
                                levels.orb_low !== null && levels.orb_low !== undefined ||
                                levels.session_high !== null && levels.session_high !== undefined ||
                                levels.session_low !== null && levels.session_low !== undefined ||
                                levels.bb_upper !== null && levels.bb_upper !== undefined ||
                                levels.bb_lower !== null && levels.bb_lower !== undefined;
            return hasAnyLevel ? (
              <div style={{ marginBottom: "1rem", padding: "0.75rem", backgroundColor: "#2c3142", borderRadius: "6px" }}>
                <div style={{ color: "#9ca3af", fontSize: "0.875rem", marginBottom: "0.5rem" }}>Key Levels:</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "0.5rem", fontSize: "0.875rem" }}>
                  {levels.current_price !== null && levels.current_price !== undefined && (
                    <div><span style={{ color: "#9ca3af" }}>Price:</span> <span style={{ color: "#ffffff", fontWeight: "600" }}>${levels.current_price.toFixed(2)}</span></div>
                  )}
                  {levels.vwap !== null && levels.vwap !== undefined && (
                    <div><span style={{ color: "#9ca3af" }}>VWAP:</span> <span style={{ color: "#ffffff", fontWeight: "600" }}>${levels.vwap.toFixed(2)}</span></div>
                  )}
                  {levels.ema20 !== null && levels.ema20 !== undefined && (
                    <div><span style={{ color: "#9ca3af" }}>EMA20:</span> <span style={{ color: "#ffffff", fontWeight: "600" }}>${levels.ema20.toFixed(2)}</span></div>
                  )}
                  {levels.ema50 !== null && levels.ema50 !== undefined && (
                    <div><span style={{ color: "#9ca3af" }}>EMA50:</span> <span style={{ color: "#ffffff", fontWeight: "600" }}>${levels.ema50.toFixed(2)}</span></div>
                  )}
                  {levels.orb_high !== null && levels.orb_high !== undefined && (
                    <div><span style={{ color: "#9ca3af" }}>ORB High:</span> <span style={{ color: "#ffffff", fontWeight: "600" }}>${levels.orb_high.toFixed(2)}</span></div>
                  )}
                  {levels.orb_low !== null && levels.orb_low !== undefined && (
                    <div><span style={{ color: "#9ca3af" }}>ORB Low:</span> <span style={{ color: "#ffffff", fontWeight: "600" }}>${levels.orb_low.toFixed(2)}</span></div>
                  )}
                  {levels.session_high !== null && levels.session_high !== undefined && (
                    <div><span style={{ color: "#9ca3af" }}>Session High:</span> <span style={{ color: "#ffffff", fontWeight: "600" }}>${levels.session_high.toFixed(2)}</span></div>
                  )}
                  {levels.session_low !== null && levels.session_low !== undefined && (
                    <div><span style={{ color: "#9ca3af" }}>Session Low:</span> <span style={{ color: "#ffffff", fontWeight: "600" }}>${levels.session_low.toFixed(2)}</span></div>
                  )}
                  {levels.bb_upper !== null && levels.bb_upper !== undefined && (
                    <div><span style={{ color: "#9ca3af" }}>BB Upper:</span> <span style={{ color: "#ffffff", fontWeight: "600" }}>${levels.bb_upper.toFixed(2)}</span></div>
                  )}
                  {levels.bb_lower !== null && levels.bb_lower !== undefined && (
                    <div><span style={{ color: "#9ca3af" }}>BB Lower:</span> <span style={{ color: "#ffffff", fontWeight: "600" }}>${levels.bb_lower.toFixed(2)}</span></div>
                  )}
                  {levels.rsi5 !== null && levels.rsi5 !== undefined && (
                    <div><span style={{ color: "#9ca3af" }}>RSI5:</span> <span style={{ color: "#ffffff", fontWeight: "600" }}>{levels.rsi5.toFixed(1)}</span></div>
                  )}
                </div>
              </div>
            ) : null;
          })()}
          <button onClick={async () => {
            try {
              setIntradayLoading(true);
              const result = await reconfirmIntraday();
              if (result.updated_recommendation) {
                setIntradayStatus(prev => prev ? {
                  ...prev,
                  current_recommendation: result.updated_recommendation
                } : null);
              }
            } catch (err: any) {
              console.error("Error reconfirming IEL:", err);
              setError(err.message || "Failed to reconfirm IEL recommendation");
            } finally {
              setIntradayLoading(false);
            }
          }} disabled={intradayLoading}
            style={{ padding: "0.5rem 1rem", backgroundColor: intradayLoading ? "#3f4758" : "#7c3aed", color: "#ffffff", border: "none", borderRadius: "6px", cursor: intradayLoading ? "not-allowed" : "pointer", fontSize: "0.875rem" }}>
            {intradayLoading ? "Reconfirming..." : "Reconfirm Now"}
          </button>
        </div>
      )}

      {/* Backtest Mode Notice */}
      {backtestMode && (
        <div style={{ padding: "1rem", backgroundColor: "#1e3a5f", border: "2px solid #2c5282", borderRadius: "8px", marginBottom: "1.5rem", color: "#9ca3af" }}>
          <p>📊 <strong style={{ color: "#ffffff" }}>Backtest Mode Active</strong> - Historical simulation running. Live engine status is hidden while backtest is active.</p>
        </div>
      )}

      {/* Action Recommendation Card */}
      {activeStatus && !backtestMode && (
        <div style={{ backgroundColor: status.badge_state ? "#7c2d12" : "#1a1d29", border: `2px solid ${status.badge_state ? "#ea580c" : "#2c3142"}`, borderRadius: "8px", padding: "1.5rem", marginBottom: "1.5rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <h3 style={{ color: "#ffffff" }}>Recommended Action ({activeEngine})</h3>
            <span style={{ padding: "0.5rem 1rem", backgroundColor: activeStatus.recommended_action_now === "HOLD" ? "#6b7280" : "#dc2626", color: "#ffffff", borderRadius: "6px", fontWeight: "600" }}>
              {activeStatus.recommended_action_now}
            </span>
          </div>
          <div style={{ color: "#d1d5db", lineHeight: "1.6", whiteSpace: "pre-line" }}>
            {activeStatus.recommended_action_detail}
          </div>
          {activeStatus.next_scheduled_action && (
            <div style={{ marginTop: "1rem", color: "#9ca3af", fontSize: "0.875rem" }}>Next: {activeStatus.next_scheduled_action}</div>
          )}
          {activeStatus.profit_pct_vs_entry !== undefined && activeStatus.profit_pct_vs_entry !== null && (
            <div style={{ marginTop: "1rem", padding: "0.75rem", backgroundColor: activeStatus.profit_pct_vs_entry >= 0 ? "#065f46" : "#7f1d1d", color: "#ffffff", borderRadius: "6px", fontWeight: "600" }}>
              Profit vs Entry: {activeStatus.profit_pct_vs_entry >= 0 ? "+" : ""}{activeStatus.profit_pct_vs_entry.toFixed(2)}%
            </div>
          )}
          {["ACCUMULATING", "HOLDING", "EXITING"].includes(activeStatus?.engine_state?.cycle_state || "") && (
            <div style={{ marginTop: "1rem" }}>
              <button
                onClick={() => {
                  setForceClosePrice(activeEngine === "SOXL" ? String(soxlPrice ?? "") : String(soxsPrice ?? ""));
                  setShowForceClose(true);
                }}
                style={{ padding: "0.5rem 1rem", backgroundColor: "#dc2626", color: "#ffffff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600" }}
              >
                Force Close Cycle
              </button>
            </div>
          )}
        </div>
      )}

      {/* Force Close Modal */}
      {showForceClose && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ backgroundColor: "#1a1d29", border: "2px solid #2c3142", borderRadius: "8px", padding: "1.5rem", maxWidth: "400px", width: "90%" }}>
            <h4 style={{ color: "#ffffff", marginBottom: "1rem" }}>Force Close Cycle ({activeEngine})</h4>
            <p style={{ color: "#9ca3af", fontSize: "0.875rem", marginBottom: "1rem" }}>
              Sell all shares and close the current cycle. This will record the exit in the Trade Ledger and Performance tab.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "1rem" }}>
              <div>
                <label style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Exit Price ($)</label>
                <input type="number" step="0.01" value={forceClosePrice} onChange={(e) => setForceClosePrice(e.target.value)}
                  style={{ display: "block", width: "100%", marginTop: "0.25rem", padding: "0.5rem", backgroundColor: "#2c3142", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px" }} />
              </div>
              <div>
                <label style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Notes (optional)</label>
                <input type="text" value={forceCloseNotes} onChange={(e) => setForceCloseNotes(e.target.value)}
                  placeholder="e.g. Manual exit"
                  style={{ display: "block", width: "100%", marginTop: "0.25rem", padding: "0.5rem", backgroundColor: "#2c3142", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px" }} />
              </div>
            </div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button onClick={handleForceClose} disabled={forceCloseLoading}
                style={{ padding: "0.5rem 1rem", backgroundColor: forceCloseLoading ? "#6b7280" : "#dc2626", color: "#ffffff", border: "none", borderRadius: "4px", cursor: forceCloseLoading ? "not-allowed" : "pointer" }}>
                {forceCloseLoading ? "Closing..." : "Confirm Force Close"}
              </button>
              <button onClick={() => { setShowForceClose(false); setForceClosePrice(""); setForceCloseNotes(""); }}
                style={{ padding: "0.5rem 1rem", backgroundColor: "#6b7280", color: "#ffffff", border: "none", borderRadius: "4px", cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cycle Phase */}
      {activeStatus?.phase && !backtestMode && (
        <div style={{ backgroundColor: "#1a1d29", border: "2px solid #2c3142", borderRadius: "8px", padding: "1.5rem", marginBottom: "1.5rem" }}>
          <h3 style={{ color: "#ffffff", marginBottom: "1rem" }}>Cycle Phase ({activeEngine})</h3>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
            <div style={{ padding: "0.75rem 1.5rem", backgroundColor: "#4c1d95", color: "#ffffff", borderRadius: "6px", fontWeight: "600", fontSize: "1.1rem" }}>
              {activeStatus.phase.phase.replace(/_/g, " ").replace("PHASE ", "")}
            </div>
            <div>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Confidence</div>
              <div style={{ color: "#ffffff", fontSize: "1.25rem", fontWeight: "600" }}>
                {activeStatus.phase.confidence}%
              </div>
            </div>
          </div>
          {activeStatus.phase.reasons && activeStatus.phase.reasons.length > 0 && (
            <div style={{ marginBottom: "1rem" }}>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem", marginBottom: "0.5rem" }}>Reasons:</div>
              <ul style={{ margin: 0, paddingLeft: "1.5rem", color: "#d1d5db" }}>
                {activeStatus.phase.reasons.map((reason, idx) => (
                  <li key={idx} style={{ marginBottom: "0.25rem" }}>{reason}</li>
                ))}
              </ul>
            </div>
          )}
          {activeStatus.phase.allowed_actions && (
            <div style={{ padding: "1rem", backgroundColor: "#2c3142", borderRadius: "6px" }}>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem", marginBottom: "0.5rem" }}>What Auto Cycle will do in this phase:</div>
              <div style={{ color: "#ffffff", fontWeight: "600" }}>{activeStatus.phase.allowed_actions.description || activeStatus.phase.description}</div>
              <div style={{ marginTop: "0.75rem", display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                {activeStatus.phase.allowed_actions.entries && (
                  <span style={{ padding: "0.25rem 0.75rem", backgroundColor: "#065f46", color: "#ffffff", borderRadius: "4px", fontSize: "0.75rem" }}>Entries</span>
                )}
                {activeStatus.phase.allowed_actions.adds && (
                  <span style={{ padding: "0.25rem 0.75rem", backgroundColor: "#065f46", color: "#ffffff", borderRadius: "4px", fontSize: "0.75rem" }}>Adds</span>
                )}
                {activeStatus.phase.allowed_actions.trims && (
                  <span style={{ padding: "0.25rem 0.75rem", backgroundColor: "#7c3aed", color: "#ffffff", borderRadius: "4px", fontSize: "0.75rem" }}>Trims</span>
                )}
                {activeStatus.phase.allowed_actions.exits && (
                  <span style={{ padding: "0.25rem 0.75rem", backgroundColor: "#dc2626", color: "#ffffff", borderRadius: "4px", fontSize: "0.75rem" }}>Exits</span>
                )}
                {!activeStatus.phase.allowed_actions.entries && !activeStatus.phase.allowed_actions.adds && !activeStatus.phase.allowed_actions.trims && !activeStatus.phase.allowed_actions.exits && (
                  <span style={{ padding: "0.25rem 0.75rem", backgroundColor: "#6b7280", color: "#ffffff", borderRadius: "4px", fontSize: "0.75rem" }}>Watchlist Only</span>
                )}
              </div>
            </div>
          )}
          {activeStatus.phase.trigger_conditions && activeStatus.phase.trigger_conditions.length > 0 && (
            <div style={{ marginTop: "1rem", padding: "1rem", backgroundColor: "#1e3a5f", borderRadius: "6px", border: "1px solid #2c5282" }}>
              <div style={{ color: "#93c5fd", fontSize: "0.875rem", marginBottom: "0.5rem", fontWeight: "600" }}>Conditions that put you in this phase:</div>
              <ul style={{ margin: 0, paddingLeft: "1.25rem", color: "#d1d5db", fontSize: "0.875rem" }}>
                {activeStatus.phase.trigger_conditions.map((c, idx) => (
                  <li key={idx} style={{ marginBottom: "0.25rem" }}>{c}</li>
                ))}
              </ul>
            </div>
          )}
          {activeStatus.phase.reversion_conditions && activeStatus.phase.reversion_conditions.length > 0 && (
            <div style={{ marginTop: "1rem", padding: "1rem", backgroundColor: "#422006", borderRadius: "6px", border: "1px solid #b45309" }}>
              <div style={{ color: "#fcd34d", fontSize: "0.875rem", marginBottom: "0.5rem", fontWeight: "600" }}>What can revert to another phase:</div>
              <ul style={{ margin: 0, paddingLeft: "1.25rem", color: "#d1d5db", fontSize: "0.875rem" }}>
                {activeStatus.phase.reversion_conditions.map((c, idx) => (
                  <li key={idx} style={{ marginBottom: "0.25rem" }}>{c}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Cycle Progress */}
      {cycleProgress && !backtestMode && (
        <div style={{ backgroundColor: "#1a1d29", border: "2px solid #2c3142", borderRadius: "8px", padding: "1.5rem", marginBottom: "1.5rem" }}>
          <h3 style={{ color: "#ffffff", marginBottom: "1rem" }}>Cycle Progress ({activeEngine})</h3>
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            {cycleProgress.legs.map((leg) => {
              const legsExecuted = activeStatus?.engine_state?.legs_executed as Array<{ leg_id?: string; shares?: number; price?: number }> | undefined;
              const legDetail = legsExecuted?.find((l) => l.leg_id === leg.id);
              const legShares = legDetail?.shares;
              const canCloseLeg = leg.executed && ["ACCUMULATING", "HOLDING", "EXITING"].includes(activeStatus?.engine_state?.cycle_state || "");
              return (
                <div key={leg.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <div style={{ padding: "0.75rem 1rem", backgroundColor: leg.executed ? "#065f46" : "#374151", color: "#ffffff", borderRadius: "6px", fontWeight: "600", minWidth: "100px", textAlign: "center" }}>
                    {leg.id.replace("leg_", "Leg ")} {leg.executed ? "✓" : "○"}
                    {leg.executed && legShares != null && (
                      <div style={{ fontSize: "0.75rem", fontWeight: "400", marginTop: "0.25rem" }}>{legShares} sh</div>
                    )}
                  </div>
                  {canCloseLeg && (
                    <button
                      onClick={() => {
                        setCloseLegId(leg.id);
                        setCloseLegPrice(activeEngine === "SOXL" ? String(soxlPrice ?? "") : String(soxsPrice ?? ""));
                        setCloseLegShares(legShares != null ? String(legShares) : "");
                        setShowCloseLeg(true);
                      }}
                      style={{ padding: "0.4rem 0.75rem", backgroundColor: "#dc2626", color: "#ffffff", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "0.8rem", fontWeight: "600" }}
                    >
                      Close
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: "1rem", display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
            <span style={{ color: "#9ca3af" }}>{cycleProgress.legs_complete} / {cycleProgress.legs_total} legs complete</span>
            {mode === "tracking" && (activeStatus?.engine_state?.real_shares ?? 0) > 0 && ["ACCUMULATING", "HOLDING", "EXITING"].includes(activeStatus?.engine_state?.cycle_state || "") && (
              <button
                onClick={() => {
                  setCloseLegId("partial");
                  setCloseLegPrice(activeEngine === "SOXL" ? String(soxlPrice ?? "") : String(soxsPrice ?? ""));
                  setCloseLegShares(String(activeStatus?.engine_state?.real_shares ?? ""));
                  setShowCloseLeg(true);
                }}
                style={{ padding: "0.4rem 0.75rem", backgroundColor: "#7c3aed", color: "#ffffff", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "0.8rem", fontWeight: "600" }}
              >
                Close Partial
              </button>
            )}
          </div>
        </div>
      )}

      {/* Close Leg Modal */}
      {showCloseLeg && closeLegId && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ backgroundColor: "#1a1d29", border: "2px solid #2c3142", borderRadius: "8px", padding: "1.5rem", maxWidth: "400px", width: "90%" }}>
            <h4 style={{ color: "#ffffff", marginBottom: "1rem" }}>{closeLegId === "partial" ? "Close Partial" : `Close ${closeLegId.replace("leg_", "Leg ")}`} ({activeEngine})</h4>
            <p style={{ color: "#9ca3af", fontSize: "0.875rem", marginBottom: "1rem" }}>
              {closeLegId === "partial" ? "Sell a portion of your position. Recorded in Trade Ledger and Performance tab." : "Sell this leg's shares. Recorded in Trade Ledger and Performance tab."}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "1rem" }}>
              {(mode === "tracking" || closeLegId === "partial") && (
                <div>
                  <label style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Shares to Close</label>
                  <input type="number" step="0.01" value={closeLegShares} onChange={(e) => setCloseLegShares(e.target.value)}
                    style={{ display: "block", width: "100%", marginTop: "0.25rem", padding: "0.5rem", backgroundColor: "#2c3142", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px" }} />
                </div>
              )}
              <div>
                <label style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Exit Price ($)</label>
                <input type="number" step="0.01" value={closeLegPrice} onChange={(e) => setCloseLegPrice(e.target.value)}
                  style={{ display: "block", width: "100%", marginTop: "0.25rem", padding: "0.5rem", backgroundColor: "#2c3142", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px" }} />
              </div>
              <div>
                <label style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Notes (optional)</label>
                <input type="text" value={closeLegNotes} onChange={(e) => setCloseLegNotes(e.target.value)}
                  placeholder="e.g. Partial exit"
                  style={{ display: "block", width: "100%", marginTop: "0.25rem", padding: "0.5rem", backgroundColor: "#2c3142", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px" }} />
              </div>
            </div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button onClick={handleCloseLeg} disabled={closeLegLoading}
                style={{ padding: "0.5rem 1rem", backgroundColor: closeLegLoading ? "#6b7280" : "#dc2626", color: "#ffffff", border: "none", borderRadius: "4px", cursor: closeLegLoading ? "not-allowed" : "pointer" }}>
                {closeLegLoading ? "Closing..." : "Confirm Close Leg"}
              </button>
              <button onClick={() => { setShowCloseLeg(false); setCloseLegId(null); setCloseLegShares(""); setCloseLegPrice(""); setCloseLegNotes(""); }}
                style={{ padding: "0.5rem 1rem", backgroundColor: "#6b7280", color: "#ffffff", border: "none", borderRadius: "4px", cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Backtest Results */}
      {backtestMode && backtestResults && (
        <div style={{ backgroundColor: "#1a1d29", border: "2px solid #2c3142", borderRadius: "8px", padding: "1.5rem", marginBottom: "1.5rem" }}>
          <h3 style={{ color: "#ffffff", marginBottom: "1rem" }}>Backtest Results ({backtestEngine})</h3>
          {backtestError && (
            <div style={{ padding: "1rem", backgroundColor: "#7f1d1d", color: "#ffffff", borderRadius: "6px", marginBottom: "1rem" }}>Error: {backtestError}</div>
          )}
          {/* IEL Overlay Comparison Metrics */}
          {backtestResults.intraday_overlay_enabled && backtestResults.comparison_metrics && (
            <div style={{ marginBottom: "1.5rem", padding: "1rem", backgroundColor: "#1e3a5f", border: "2px solid #4c1d95", borderRadius: "8px" }}>
              <h4 style={{ color: "#ffffff", marginBottom: "0.75rem" }}>IEL Overlay Comparison</h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
                <div>
                  <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Auto Cycle Only</div>
                  <div style={{ color: "#ffffff", fontSize: "1rem", fontWeight: "600" }}>
                    P&L: ${backtestResults.comparison_metrics.auto_cycle_only.total_pnl.toFixed(2)}
                  </div>
                  <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>
                    Return: {backtestResults.comparison_metrics.auto_cycle_only.total_return_pct.toFixed(2)}%
                  </div>
                </div>
                <div>
                  <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>With IEL Overlay</div>
                  <div style={{ color: "#ffffff", fontSize: "1rem", fontWeight: "600" }}>
                    P&L: ${backtestResults.comparison_metrics.with_iel_overlay.total_pnl.toFixed(2)}
                  </div>
                  <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>
                    Return: {backtestResults.comparison_metrics.with_iel_overlay.total_return_pct.toFixed(2)}%
                  </div>
                </div>
                <div>
                  <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>IEL Improvement</div>
                  <div style={{ color: backtestResults.comparison_metrics.incremental.pnl_improvement >= 0 ? "#10b981" : "#ef4444", fontSize: "1rem", fontWeight: "600" }}>
                    ${backtestResults.comparison_metrics.incremental.pnl_improvement.toFixed(2)}
                  </div>
                  <div style={{ color: backtestResults.comparison_metrics.incremental.return_pct_improvement >= 0 ? "#10b981" : "#ef4444", fontSize: "0.875rem" }}>
                    {backtestResults.comparison_metrics.incremental.return_pct_improvement >= 0 ? "+" : ""}{backtestResults.comparison_metrics.incremental.return_pct_improvement.toFixed(2)}%
                  </div>
                </div>
              </div>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>
                IEL Actions: {backtestResults.comparison_metrics.with_iel_overlay.iel_legs_count} legs, {backtestResults.comparison_metrics.with_iel_overlay.iel_trims_count} trims
              </div>
            </div>
          )}
          
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
            <div>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Cycle Status</div>
              <div style={{ color: "#ffffff", fontSize: "1.25rem", fontWeight: "600" }}>
                {backtestResults.cycle_completed ? "✓ Completed" : backtestResults.final_state || "In Progress"}
              </div>
            </div>
            {backtestResults.entry_date && (
              <div>
                <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Entry Date</div>
                <div style={{ color: "#ffffff", fontSize: "1.25rem", fontWeight: "600" }}>
                  {new Date(backtestResults.entry_date).toLocaleDateString()}
                </div>
              </div>
            )}
            {backtestResults.exit_date && (
              <div>
                <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Exit Date</div>
                <div style={{ color: "#ffffff", fontSize: "1.25rem", fontWeight: "600" }}>
                  {new Date(backtestResults.exit_date).toLocaleDateString()}
                </div>
              </div>
            )}
            <div>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Total P&L</div>
              <div style={{ color: (backtestResults.total_pnl || 0) >= 0 ? "#10b981" : "#ef4444", fontSize: "1.25rem", fontWeight: "600" }}>
                ${(backtestResults.total_pnl || 0).toFixed(2)}
              </div>
            </div>
            <div>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Total Return</div>
              <div style={{ color: (backtestResults.total_return_pct || 0) >= 0 ? "#10b981" : "#ef4444", fontSize: "1.25rem", fontWeight: "600" }}>
                {(backtestResults.total_return_pct || 0).toFixed(2)}%
              </div>
            </div>
            <div>
              <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>Total Invested</div>
              <div style={{ color: "#ffffff", fontSize: "1.25rem", fontWeight: "600" }}>
                ${(backtestResults.total_invested || 0).toFixed(2)}
              </div>
            </div>
          </div>
          {backtestResults.legs_executed && backtestResults.legs_executed.length > 0 && (
            <div style={{ marginBottom: "1.5rem" }}>
              <h4 style={{ color: "#ffffff", marginBottom: "0.75rem" }}>Buy Legs Executed</h4>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #3f4758" }}>
                      <th style={{ padding: "0.5rem", textAlign: "left", color: "#9ca3af" }}>Leg</th>
                      <th style={{ padding: "0.5rem", textAlign: "left", color: "#9ca3af" }}>Date</th>
                      {backtestResults.intraday_overlay_enabled && (
                        <th style={{ padding: "0.5rem", textAlign: "left", color: "#9ca3af" }}>Style</th>
                      )}
                      <th style={{ padding: "0.5rem", textAlign: "right", color: "#9ca3af" }}>Shares</th>
                      <th style={{ padding: "0.5rem", textAlign: "right", color: "#9ca3af" }}>Price</th>
                      <th style={{ padding: "0.5rem", textAlign: "right", color: "#9ca3af" }}>Notional</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backtestResults.legs_executed.map((leg: any, idx: number) => {
                      const dateParts = leg.execution_date.split('-');
                      const execDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
                      const isWeekend = leg.weekday !== undefined && leg.weekday >= 5;
                      return (
                        <tr key={idx} style={{ borderBottom: "1px solid #2c3142", backgroundColor: isWeekend ? "#7f1d1d" : "transparent" }}>
                          <td style={{ padding: "0.5rem", color: "#ffffff" }}>{leg.leg_id || `Leg ${idx + 1}`}</td>
                          <td style={{ padding: "0.5rem", color: isWeekend ? "#ef4444" : "#9ca3af" }}>
                            {execDate.toLocaleDateString()}
                            {leg.execution_time && typeof leg.execution_time === "string" && (
                              <span style={{ fontSize: "0.75rem", marginLeft: "0.25rem", opacity: 0.7 }}>
                                {new Date(leg.execution_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                            {isWeekend && <span style={{ color: "#ef4444", marginLeft: "0.5rem" }}>⚠️ WEEKEND</span>}
                            {leg.day_name && <span style={{ fontSize: "0.75rem", marginLeft: "0.5rem", opacity: 0.7 }}>({leg.day_name})</span>}
                          </td>
                          {backtestResults.intraday_overlay_enabled && (
                            <td style={{ padding: "0.5rem" }}>
                              {leg.trade_style === "INTRADAY_EXEC" ? (
                                <span style={{ padding: "0.25rem 0.5rem", backgroundColor: "#7c3aed", color: "#ffffff", borderRadius: "4px", fontSize: "0.75rem", fontWeight: "600" }}>
                                  IEL
                                </span>
                              ) : (
                                <span style={{ padding: "0.25rem 0.5rem", backgroundColor: "#6366f1", color: "#ffffff", borderRadius: "4px", fontSize: "0.75rem", fontWeight: "600" }}>
                                  Cycle
                                </span>
                              )}
                            </td>
                          )}
                          <td style={{ padding: "0.5rem", textAlign: "right", color: "#ffffff" }}>{leg.shares}</td>
                          <td style={{ padding: "0.5rem", textAlign: "right", color: "#ffffff" }}>${leg.price.toFixed(2)}</td>
                          <td style={{ padding: "0.5rem", textAlign: "right", color: "#ffffff" }}>${leg.notional.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {backtestResults.exits && backtestResults.exits.length > 0 && (
            <div style={{ marginBottom: "1.5rem" }}>
              <h4 style={{ color: "#ffffff", marginBottom: "0.75rem" }}>Exits Executed</h4>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #3f4758" }}>
                      <th style={{ padding: "0.5rem", textAlign: "left", color: "#9ca3af" }}>Exit</th>
                      <th style={{ padding: "0.5rem", textAlign: "left", color: "#9ca3af" }}>Date</th>
                      {backtestResults.intraday_overlay_enabled && (
                        <th style={{ padding: "0.5rem", textAlign: "left", color: "#9ca3af" }}>Style</th>
                      )}
                      <th style={{ padding: "0.5rem", textAlign: "right", color: "#9ca3af" }}>Shares</th>
                      <th style={{ padding: "0.5rem", textAlign: "right", color: "#9ca3af" }}>Price</th>
                      <th style={{ padding: "0.5rem", textAlign: "right", color: "#9ca3af" }}>P&L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backtestResults.exits.map((exit: any, idx: number) => {
                      const dateParts = exit.execution_date.split('-');
                      const execDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
                      const isWeekend = exit.weekday !== undefined && exit.weekday >= 5;
                      return (
                        <tr key={idx} style={{ borderBottom: "1px solid #2c3142", backgroundColor: isWeekend ? "#7f1d1d" : "transparent" }}>
                          <td style={{ padding: "0.5rem", color: "#ffffff" }}>{exit.exit_type || exit.exit_id || `Exit ${idx + 1}`}</td>
                          <td style={{ padding: "0.5rem", color: isWeekend ? "#ef4444" : "#9ca3af" }}>
                            {execDate.toLocaleDateString()}
                            {exit.execution_time && typeof exit.execution_time === "string" && (
                              <span style={{ fontSize: "0.75rem", marginLeft: "0.25rem", opacity: 0.7 }}>
                                {new Date(exit.execution_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                            {isWeekend && <span style={{ color: "#ef4444", marginLeft: "0.5rem" }}>⚠️ WEEKEND</span>}
                            {exit.day_name && <span style={{ fontSize: "0.75rem", marginLeft: "0.5rem", opacity: 0.7 }}>({exit.day_name})</span>}
                          </td>
                          {backtestResults.intraday_overlay_enabled && (
                            <td style={{ padding: "0.5rem" }}>
                              {exit.trade_style === "INTRADAY_EXEC" ? (
                                <span style={{ padding: "0.25rem 0.5rem", backgroundColor: "#7c3aed", color: "#ffffff", borderRadius: "4px", fontSize: "0.75rem", fontWeight: "600" }}>
                                  IEL
                                </span>
                              ) : (
                                <span style={{ padding: "0.25rem 0.5rem", backgroundColor: "#6366f1", color: "#ffffff", borderRadius: "4px", fontSize: "0.75rem", fontWeight: "600" }}>
                                  Cycle
                                </span>
                              )}
                            </td>
                          )}
                          <td style={{ padding: "0.5rem", textAlign: "right", color: "#ffffff" }}>{exit.shares}</td>
                          <td style={{ padding: "0.5rem", textAlign: "right", color: "#ffffff" }}>${exit.price.toFixed(2)}</td>
                          <td style={{ padding: "0.5rem", textAlign: "right", color: exit.realized_pnl >= 0 ? "#10b981" : "#ef4444" }}>
                            ${exit.realized_pnl ? exit.realized_pnl.toFixed(2) : "0.00"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Trade Ledger */}
      {enabled && !backtestMode && (
        <div style={{ backgroundColor: "#1a1d29", border: "2px solid #2c3142", borderRadius: "8px", padding: "1.5rem", marginBottom: "1.5rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <h3 style={{ color: "#ffffff" }}>Trade Ledger</h3>
            <div style={{ display: "flex", gap: "1rem" }}>
              <select value={ledgerEngine || ""} onChange={(e) => setLedgerEngine(e.target.value ? e.target.value as "SOXL" | "SOXS" : undefined)}
                style={{ padding: "0.5rem", backgroundColor: "#2c3142", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px" }}>
                <option value="">All Engines</option>
                <option value="SOXL">SOXL</option>
                <option value="SOXS">SOXS</option>
              </select>
              <select value={ledgerType} onChange={(e) => setLedgerType(e.target.value as "simulation" | "real")}
                style={{ padding: "0.5rem", backgroundColor: "#2c3142", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px" }}>
                <option value="simulation">Simulation</option>
                <option value="real">Real</option>
              </select>
              {mode === "tracking" && (
                <button onClick={() => setShowRecordTrade(true)}
                  style={{ padding: "0.5rem 1rem", backgroundColor: "#6366f1", color: "#ffffff", border: "none", borderRadius: "4px", cursor: "pointer" }}>
                  Record Trade
                </button>
              )}
            </div>
          </div>

          {showRecordTrade && (
            <div style={{ padding: "1rem", backgroundColor: "#2c3142", borderRadius: "6px", marginBottom: "1rem" }}>
              <h4 style={{ color: "#ffffff", marginBottom: "0.75rem" }}>Record Manual Trade</h4>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                <div style={{ display: "flex", gap: "1rem" }}>
                  <label style={{ color: "#9ca3af", minWidth: "100px" }}>Engine:</label>
                  <select value={recordEngine} onChange={(e) => setRecordEngine(e.target.value as "SOXL" | "SOXS")}
                    style={{ padding: "0.5rem", backgroundColor: "#1a1d29", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px" }}>
                    <option value="SOXL">SOXL</option>
                    <option value="SOXS">SOXS</option>
                  </select>
                </div>
                <div style={{ display: "flex", gap: "1rem" }}>
                  <label style={{ color: "#9ca3af", minWidth: "100px" }}>Action:</label>
                  <select value={recordAction} onChange={(e) => setRecordAction(e.target.value as "BUY" | "SELL")}
                    style={{ padding: "0.5rem", backgroundColor: "#1a1d29", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px" }}>
                    <option value="BUY">BUY</option>
                    <option value="SELL">SELL</option>
                  </select>
                </div>
                <div style={{ display: "flex", gap: "1rem" }}>
                  <label style={{ color: "#9ca3af", minWidth: "100px" }}>Shares:</label>
                  <input type="number" value={recordShares} onChange={(e) => setRecordShares(e.target.value)}
                    style={{ padding: "0.5rem", backgroundColor: "#1a1d29", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px", flex: 1 }} />
                </div>
                <div style={{ display: "flex", gap: "1rem" }}>
                  <label style={{ color: "#9ca3af", minWidth: "100px" }}>Price ($):</label>
                  <input type="number" step="0.01" value={recordPrice} onChange={(e) => setRecordPrice(e.target.value)}
                    style={{ padding: "0.5rem", backgroundColor: "#1a1d29", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px", flex: 1 }} />
                </div>
                <div style={{ display: "flex", gap: "1rem" }}>
                  <label style={{ color: "#9ca3af", minWidth: "100px" }}>Notes:</label>
                  <input type="text" value={recordNotes} onChange={(e) => setRecordNotes(e.target.value)}
                    style={{ padding: "0.5rem", backgroundColor: "#1a1d29", color: "#ffffff", border: "1px solid #3f4758", borderRadius: "4px", flex: 1 }} />
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button onClick={handleRecordTrade}
                    style={{ padding: "0.5rem 1rem", backgroundColor: "#22c55e", color: "#ffffff", border: "none", borderRadius: "4px", cursor: "pointer" }}>
                    Save
                  </button>
                  <button onClick={() => { setShowRecordTrade(false); setRecordShares(""); setRecordPrice(""); setRecordNotes(""); }}
                    style={{ padding: "0.5rem 1rem", backgroundColor: "#6b7280", color: "#ffffff", border: "none", borderRadius: "4px", cursor: "pointer" }}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {ledgerLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}><LoadingSpinner /></div>
          ) : ledger.length === 0 ? (
            <div style={{ color: "#9ca3af", textAlign: "center", padding: "2rem" }}>No trades recorded yet</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #3f4758" }}>
                    <th style={{ padding: "0.75rem", textAlign: "left", color: "#9ca3af" }}>Engine</th>
                    <th style={{ padding: "0.75rem", textAlign: "left", color: "#9ca3af" }}>Timestamp</th>
                    <th style={{ padding: "0.75rem", textAlign: "left", color: "#9ca3af" }}>Style</th>
                    <th style={{ padding: "0.75rem", textAlign: "left", color: "#9ca3af" }}>Action</th>
                    <th style={{ padding: "0.75rem", textAlign: "left", color: "#9ca3af" }}>Leg</th>
                    <th style={{ padding: "0.75rem", textAlign: "right", color: "#9ca3af" }}>Shares</th>
                    <th style={{ padding: "0.75rem", textAlign: "right", color: "#9ca3af" }}>Price</th>
                    <th style={{ padding: "0.75rem", textAlign: "right", color: "#9ca3af" }}>Notional</th>
                    <th style={{ padding: "0.75rem", textAlign: "right", color: "#9ca3af" }}>Position</th>
                    <th style={{ padding: "0.75rem", textAlign: "right", color: "#9ca3af" }}>Avg Entry</th>
                    <th style={{ padding: "0.75rem", textAlign: "right", color: "#9ca3af" }}>P&L</th>
                    <th style={{ padding: "0.75rem", textAlign: "left", color: "#9ca3af" }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.map((entry) => {
                    const tradeStyle = (entry as any).trade_style || "AUTO_CYCLE";
                    const actionSubtype = (entry as any).action_subtype;
                    return (
                    <tr key={entry.id} style={{ borderBottom: "1px solid #2c3142" }}>
                      <td style={{ padding: "0.75rem", color: entry.engine_key === "SOXL" ? "#22c55e" : "#ef4444", fontWeight: "600" }}>{entry.engine_key}</td>
                      <td style={{ padding: "0.75rem", color: "#d1d5db" }}>{new Date(entry.timestamp).toLocaleString()}</td>
                      <td style={{ padding: "0.75rem" }}>
                        <span style={{ 
                          padding: "0.25rem 0.5rem", 
                          backgroundColor: tradeStyle === "INTRADAY_EXEC" ? "#7c3aed" : "#6366f1", 
                          color: "#ffffff", 
                          borderRadius: "4px", 
                          fontSize: "0.75rem",
                          fontWeight: "600"
                        }}>
                          {tradeStyle === "INTRADAY_EXEC" ? "IEL" : "Cycle"}
                        </span>
                        {actionSubtype && (
                          <span style={{ marginLeft: "0.25rem", color: "#9ca3af", fontSize: "0.75rem" }}>({actionSubtype})</span>
                        )}
                      </td>
                      <td style={{ padding: "0.75rem", color: entry.action === "BUY" ? "#22c55e" : "#ef4444" }}>{entry.action}</td>
                      <td style={{ padding: "0.75rem", color: "#d1d5db" }}>{entry.leg_id || "-"}</td>
                      <td style={{ padding: "0.75rem", textAlign: "right", color: "#d1d5db" }}>{entry.shares.toFixed(2)}</td>
                      <td style={{ padding: "0.75rem", textAlign: "right", color: "#d1d5db" }}>${entry.price.toFixed(2)}</td>
                      <td style={{ padding: "0.75rem", textAlign: "right", color: "#d1d5db" }}>${entry.notional.toFixed(2)}</td>
                      <td style={{ padding: "0.75rem", textAlign: "right", color: "#d1d5db" }}>{entry.position_shares_after.toFixed(2)}</td>
                      <td style={{ padding: "0.75rem", textAlign: "right", color: "#d1d5db" }}>{entry.avg_entry_price_after ? `$${entry.avg_entry_price_after.toFixed(2)}` : "-"}</td>
                      <td style={{ padding: "0.75rem", textAlign: "right", color: entry.realized_pnl && entry.realized_pnl >= 0 ? "#22c55e" : "#ef4444" }}>
                        {entry.realized_pnl !== undefined && entry.realized_pnl !== null ? `$${entry.realized_pnl.toFixed(2)}` : "-"}
                      </td>
                      <td style={{ padding: "0.75rem", color: "#9ca3af", fontSize: "0.875rem" }}>{entry.notes || "-"}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AutoCycle;

